<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
        <h2 class="text-xl font-bold">Post Surgery Locations</h2>
        <button @click="openForm" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          + New Location
        </button>
      </div>

      <div v-if="masterDataStore.postSurgeryLocations.length === 0" class="text-center py-12 text-gray-500">
        No post surgery locations found
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b">
            <tr>
              <th class="px-6 py-3 text-left text-sm font-semibold">ID</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Name</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Description</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Status</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="location in masterDataStore.postSurgeryLocations" :key="location.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 text-sm text-gray-500">{{ location.id }}</td>
              <td class="px-6 py-4 text-sm font-medium">{{ location.name }}</td>
              <td class="px-6 py-4 text-sm">{{ location.description || '-' }}</td>
              <td class="px-6 py-4 text-sm">
                <span :class="[
                  'px-2 py-1 rounded text-xs',
                  String(location.is_active) === '1' || location.is_active === true ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]">
                  {{ String(location.is_active) === '1' || location.is_active === true ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm space-x-2">
                <button @click="editLocation(location)" class="text-blue-600 hover:text-blue-800">Edit</button>
                <button @click="deleteLocation(location.id)" class="text-red-600 hover:text-red-800">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md z-50 relative">
        <h3 class="text-lg font-bold mb-4">{{ editingLocation ? 'Edit Location' : 'New Location' }}</h3>
        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1">Name *</label>
            <input v-model="formData.name" required class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Description</label>
            <input v-model="formData.description" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <label class="flex items-center gap-2 text-sm">
            <input v-model="formData.is_active" type="checkbox" class="w-4 h-4" />
            Active
          </label>
          <div class="flex justify-end gap-2 pt-4 border-t">
            <button type="button" @click="closeForm" class="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
            <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              {{ editingLocation ? 'Update' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { useMasterDataStore } from '@/stores/masterDataStore'

const masterDataStore = useMasterDataStore()
const showForm = ref(false)
const editingLocation = ref(null)
const formData = ref({ name: '', description: '', is_active: true })

const openForm = () => {
  showForm.value = true
}

const closeForm = () => {
  showForm.value = false
  editingLocation.value = null
  formData.value = { name: '', description: '', is_active: true }
}

const editLocation = (location) => {
  editingLocation.value = location
  formData.value = {
    name: location.name || '',
    description: location.description || '',
    is_active: String(location.is_active) === '1' || location.is_active === true,
  }
  showForm.value = true
}

const submitForm = async () => {
  try {
    const data = {
      name: formData.value.name.trim(),
      description: formData.value.description || '',
      is_active: formData.value.is_active ? 1 : 0,
    }

    if (editingLocation.value) {
      await masterDataStore.updatePostSurgeryLocation(editingLocation.value.id, data)
      Swal.fire({ icon: 'success', title: 'Success', text: 'Location updated successfully', confirmButtonColor: '#3085d6' })
    } else {
      await masterDataStore.createPostSurgeryLocation(data)
      Swal.fire({ icon: 'success', title: 'Success', text: 'Location created successfully', confirmButtonColor: '#3085d6' })
    }

    closeForm()
    await masterDataStore.getPostSurgeryLocations()
  } catch (error) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || masterDataStore.error || 'Failed to save location', confirmButtonColor: '#3085d6' })
  }
}

const deleteLocation = async (id) => {
  const result = await Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this location?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel',
  })
  if (!result.isConfirmed) return

  try {
    await masterDataStore.deletePostSurgeryLocation(id)
    Swal.fire({ icon: 'success', title: 'Deleted', text: 'Location deleted successfully', confirmButtonColor: '#3085d6' })
  } catch (error) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || masterDataStore.error || 'Failed to delete location', confirmButtonColor: '#3085d6' })
  }
}

onMounted(() => {
  masterDataStore.getPostSurgeryLocations()
})
</script>
